import React from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Icon,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_BUTTON_DEFAULT, V4_DIALOG_CONTENT, V4_DIALOG_MD } from '../../theme/v4Compat';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { CopyToClipboardButton } from '../../elements';

interface GenerateApiKeyDialogProps {
  open: boolean;
  slaughterhouseId: string;
  onClose: (keyGenerated: boolean) => void;
}

const GenerateApiKeyDialog: React.FC<GenerateApiKeyDialogProps> = ({ open, slaughterhouseId, onClose }) => {
  const styles = sxStyles(useTheme());
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [step, setStep] = React.useState<'input' | 'display'>('input');
  const [keyName, setKeyName] = React.useState('');
  const [keyDescription, setKeyDescription] = React.useState('');
  const [expirationOption, setExpirationOption] = React.useState<string>('no-expiration');
  const [generatedKey, setGeneratedKey] = React.useState<string | null>(null);
  const [generatedKeyData, setGeneratedKeyData] = React.useState<any>(null);
  const [keyCopied, setKeyCopied] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const handleCancel = () => {
    resetState();
    onClose(false);
  };

  const resetState = () => {
    setStep('input');
    setKeyName('');
    setKeyDescription('');
    setExpirationOption('no-expiration');
    setGeneratedKey(null);
    setGeneratedKeyData(null);
    setKeyCopied(false);
  };

  const calculateExpirationDate = (option: string): string | null => {
    if (option === 'no-expiration') {
      return null;
    }

    const now = new Date();
    let daysToAdd = 0;

    switch (option) {
      case '30-days':
        daysToAdd = 30;
        break;
      case '60-days':
        daysToAdd = 60;
        break;
      case '90-days':
        daysToAdd = 90;
        break;
      case '180-days':
        daysToAdd = 180;
        break;
      case '365-days':
        daysToAdd = 365;
        break;
      default:
        return null;
    }

    now.setDate(now.getDate() + daysToAdd);
    return now.toISOString();
  };

  const handleGenerate = () => {
    if (!keyName || keyName.trim().length === 0) {
      notifications.error(t('f-val-api-key-name'));
      return;
    }

    const expirationDate = calculateExpirationDate(expirationOption);

    setIsBusy(true);
    apiService.api.slaughterhouses
      .slaughterhousesGenerateApiKey(
        { id: slaughterhouseId },
        {
          name: keyName,
          description: keyDescription || null,
          expiresAt: expirationDate
        }
      )
      .then((result) => {
        setGeneratedKey(result.data.apiKey); // CRITICAL: One-time access to plaintext key
        setGeneratedKeyData(result.data);
        setStep('display');
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleDownloadKey = () => {
    if (!generatedKey || !generatedKeyData) return;

    const blob = new Blob([generatedKey], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${generatedKeyData.name.replace(/\s+/g, '_')}_api_key.txt`;
    link.click();
    URL.revokeObjectURL(url);
    notifications.success(t('f-slaughterhouse-api-key-downloaded'));
  };

  const handleClose = () => {
    if (step === 'display' && !keyCopied) {
      return; // Prevent closing without confirmation
    }
    resetState();
    onClose(step === 'display'); // Signal to parent to reload keys if a key was generated
  };

  const handleDialogClose = (_event: object, reason: string) => {
    // Prevent closing by clicking outside or pressing escape when key is displayed and not copied
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (step === 'display' && !keyCopied) {
        return;
      }
    }
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth sx={V4_DIALOG_MD}>
      <DialogTitle>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item>
            <Icon color="primary">vpn_key</Icon>
          </Grid>
          <Grid item>{t('f-slaughterhouse-api-key-generate-title')}</Grid>
        </Grid>
      </DialogTitle>

      {step === 'input' ? (
        <>
          <DialogContent sx={V4_DIALOG_CONTENT}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('f-slaughterhouse-api-key-name')}
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  disabled={isBusy}
                  helperText={t('f-slaughterhouse-api-key-name-helper') || 'A descriptive name for this API key'}
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t('f-slaughterhouse-api-key-description')}
                  value={keyDescription}
                  onChange={(e) => setKeyDescription(e.target.value)}
                  disabled={isBusy}
                  helperText={
                    t('f-slaughterhouse-api-key-description-helper') ||
                    'Optional: Additional details about this key and its purpose'
                  }
                  variant="standard"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={isBusy} variant="standard">
                  <InputLabel id="expiration-select-label">{t('f-slaughterhouse-api-key-expires')}</InputLabel>
                  <Select
                    labelId="expiration-select-label"
                    value={expirationOption}
                    onChange={(e) => setExpirationOption(e.target.value as string)}
                  >
                    <MenuItem value="no-expiration">
                      {t('f-slaughterhouse-api-key-no-expiration') || 'No Expiration'}
                    </MenuItem>
                    <MenuItem value="30-days">{t('f-slaughterhouse-api-key-30-days') || '30 Days'}</MenuItem>
                    <MenuItem value="60-days">{t('f-slaughterhouse-api-key-60-days') || '60 Days'}</MenuItem>
                    <MenuItem value="90-days">{t('f-slaughterhouse-api-key-90-days') || '90 Days'}</MenuItem>
                    <MenuItem value="180-days">
                      {t('f-slaughterhouse-api-key-180-days') || '180 Days (6 Months)'}
                    </MenuItem>
                    <MenuItem value="365-days">
                      {t('f-slaughterhouse-api-key-365-days') || '365 Days (1 Year)'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={isBusy} color="inherit" sx={V4_BUTTON_DEFAULT}>
              {t('f-com-cancel')}
            </Button>
            <Button
              onClick={handleGenerate}
              color="primary"
              variant="contained"
              disabled={!keyName || keyName.trim().length === 0 || isBusy}
            >
              {isBusy ? <CircularProgress size={24} /> : t('f-com-generate')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent sx={V4_DIALOG_CONTENT}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="error" sx={styles.criticalAlert}>
                  <AlertTitle>{t('f-slaughterhouse-api-key-warning-title')}</AlertTitle>
                  {t('f-slaughterhouse-api-key-copy-warning')}
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={generatedKey}
                  slotProps={{
                    input: {
                      readOnly: true,
                      sx: styles.keyDisplay,
                      endAdornment: (
                        <InputAdornment position="end">
                          <CopyToClipboardButton text={generatedKey || ''} />
                        </InputAdornment>
                      )
                    }
                  }}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item>
                    <Button
                      startIcon={<Icon>download</Icon>}
                      onClick={handleDownloadKey}
                      variant="outlined"
                      color="primary"
                    >
                      {t('f-slaughterhouse-api-key-download')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox checked={keyCopied} onChange={(e) => setKeyCopied(e.target.checked)} color="primary" />
                  }
                  label={t('f-slaughterhouse-api-key-copied-confirmation')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={!keyCopied} variant="contained" color="primary">
              {t('f-com-close')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default GenerateApiKeyDialog;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    ml8: {
      marginLeft: theme.spacing(1)
    },
    criticalAlert: {
      marginBottom: theme.spacing(2)
    },
    keyDisplay: {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2)
    },
    mt2: {
      marginTop: theme.spacing(2)
    }
  }) as const;
